import React, { useEffect, useState } from "react";
import ClientPortalEmailIcon from "../../../../Assets/CustomerDetailPage/ClientPortalEmailIcon.svg";
import { useCustomerStore } from "../../../../Store/Customer";
import { ReactComponent as CopyButtonIcon } from "../../../../Assets/ActionIcons/CopyButtonIcon.svg";
import { ReactComponent as EditIcon } from "../../../../Assets/ActionIcons/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../Assets/ActionIcons/DeleteIcon.svg";
import { ReactComponent as PlusIcon } from "../../../../Assets/ActionIcons/PlusIcon.svg";
import ViewMoreIcon from "../../../../Assets/ActionIcons/ViewMoreIcon.svg";
import ViewLessIcon from "../../../../Assets/ActionIcons/ViewLessIcon.svg";
import { PaymentsClientPortalWhitelistView } from "../../../../Contracts/Customer";
import { useAuth0 } from "@auth0/auth0-react";
import { useTeamStore } from "../../../../Store/Team";
import { validateDomain, validateEmail } from "../../../../Contracts/Common";

const CustomerClientPortalCard: React.FC = () => {
  const {
    customerDetailEntity,
    isLoading: customerIsLoading,
    editWhiteList,
    createWhiteList,
    deleteWhiteList
  } = useCustomerStore();
  const { isLoading: teamIsLoading, getCurrentTeam, currentTeam } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => {
    getCurrentTeam(getAccessTokenSilently);
  }, [getCurrentTeam, getAccessTokenSilently]);
  const [whiteListItemBeingEdited, setWhiteListItemBeingEdited] = useState<{
    id: number;
    isLoading: boolean;
    emailOrDomain: string;
    emailOrDomainValid: boolean;
  }>({
    id: -1,
    isLoading: false,
    emailOrDomain: "",
    emailOrDomainValid: true
  });
  const [whiteListItemBeingCreated, setWhiteListItemBeingCreated] = useState<{
    creating: boolean;
    isLoading: boolean;
    emailOrDomain: string;
    emailOrDomainValid: boolean;
  }>({
    creating: false,
    isLoading: false,
    emailOrDomain: "",
    emailOrDomainValid: true
  });
  const portalLink = currentTeam?.partner_white_label_metadata.client_portal_uuid
    ? `${window.location.origin}/client-portal/${currentTeam.partner_white_label_metadata.client_portal_uuid}`
    : `${window.location.origin}/client-portal`;
  const [showPopover, setShowPopover] = useState(false);
  const [viewMore, setViewMore] = useState(false);

  const whitelistsToShow = viewMore
    ? customerDetailEntity?.whitelists?.data || []
    : (customerDetailEntity?.whitelists?.data || []).slice(0, 2);

  const portalLinkCopyToClipboard = async () => {
    await navigator.clipboard.writeText(portalLink);
    setShowPopover(true);
    setTimeout(() => setShowPopover(false), 3000);
  };

  const onWhiteListEdit = (whitelist: PaymentsClientPortalWhitelistView) => async () => {
    if (!validateField()) return;
    if (whiteListItemBeingEdited.emailOrDomain) {
      setWhiteListItemBeingEdited({
        ...whiteListItemBeingEdited,
        isLoading: true
      });
      await editWhiteList(
        getAccessTokenSilently,
        whitelist.id,
        whiteListItemBeingEdited.emailOrDomain
      );
    }
    setWhiteListItemBeingEdited({
      id: -1,
      isLoading: false,
      emailOrDomain: "",
      emailOrDomainValid: true
    });
  };

  const onWhiteListCreate = async () => {
    if (!validateField()) return;
    if (whiteListItemBeingCreated.emailOrDomain && customerDetailEntity?.id) {
      setWhiteListItemBeingCreated({
        ...whiteListItemBeingCreated,
        isLoading: true
      });
      await createWhiteList(
        getAccessTokenSilently,
        customerDetailEntity.id,
        whiteListItemBeingCreated.emailOrDomain
      );
    }
    setWhiteListItemBeingCreated({
      creating: false,
      isLoading: false,
      emailOrDomain: "",
      emailOrDomainValid: true
    });
  };

  const onWhiteListDelete = (whitelist: PaymentsClientPortalWhitelistView) => async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this item?");
    if (confirmDelete) {
      await deleteWhiteList(getAccessTokenSilently, whitelist.id);
    }
  };

  const validateField = (): boolean => {
    if (whiteListItemBeingCreated.creating) {
      const validEmailOrDomain =
        validateDomain(whiteListItemBeingCreated.emailOrDomain) ||
        validateEmail(whiteListItemBeingCreated.emailOrDomain);
      setWhiteListItemBeingCreated({
        ...whiteListItemBeingCreated,
        emailOrDomainValid: validEmailOrDomain
      });
      return validEmailOrDomain;
    }
    if (whiteListItemBeingEdited.id !== -1) {
      const validEmailOrDomain =
        validateDomain(whiteListItemBeingEdited.emailOrDomain) ||
        validateEmail(whiteListItemBeingEdited.emailOrDomain);
      setWhiteListItemBeingEdited({
        ...whiteListItemBeingEdited,
        emailOrDomainValid: validEmailOrDomain
      });
      return validEmailOrDomain;
    }
    return true;
  };

  return (
    <div className="rounded p-3 border border-gray-300 my-2">
      <div className="flex items-center justify-between mb-4">
        <span className="font-semibold">Approved for client portal</span>
        <button
          onClick={() =>
            setWhiteListItemBeingCreated({
              ...whiteListItemBeingCreated,
              creating: true
            })
          }
          className="text-blue-600 hover:text-blue-700 hover:scale-105 transition-transform flex items-center text-left"
        >
          <PlusIcon className="h-4 w-4 mx-2" />
          <span> New Client Email / Domain</span>
        </button>
      </div>
      {whiteListItemBeingCreated.creating && (
        <div className="p-2 border-t border-gray-300">
          <label className="mb-2">
            Email / Domain<sup className="text-red-600">*</sup>
          </label>

          <div className="flex items-start justify-between">
            <div className="flex flex-col">
              <input
                type="text"
                className="p-1 border border-gray-300 rounded"
                onChange={e =>
                  setWhiteListItemBeingCreated({
                    ...whiteListItemBeingCreated,
                    emailOrDomain: e.target.value
                  })
                }
                disabled={whiteListItemBeingCreated.isLoading}
              />
              {!whiteListItemBeingCreated.emailOrDomainValid && (
                <span className="italic text-red-600">Please enter a valid email or domain</span>
              )}
            </div>
            <div className="flex items-center">
              <button
                onClick={() =>
                  setWhiteListItemBeingCreated({
                    creating: false,
                    isLoading: false,
                    emailOrDomain: "",
                    emailOrDomainValid: true
                  })
                }
                className={`text-red-600 mr-4 hover:text-blue-600 hover:scale-110 transition-transform ${
                  whiteListItemBeingCreated.isLoading && "cursor-progress"
                }`}
                disabled={whiteListItemBeingCreated.isLoading}
              >
                Discard
              </button>
              <button
                onClick={onWhiteListCreate}
                className={`hover:text-blue-500 hover:scale-110 transition-transform ${
                  whiteListItemBeingCreated.isLoading && "cursor-progress"
                }`}
                disabled={whiteListItemBeingCreated.isLoading}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {whitelistsToShow.map(whitelist => (
        <div className="p-2 border-t border-gray-300">
          {whiteListItemBeingEdited.id === whitelist.id && (
            <label className="mb-2">
              Email / Domain<sup className="text-red-600">*</sup>
            </label>
          )}
          <div
            className={`${
              customerIsLoading ? "hidden" : "visible"
            } flex items-start justify-between`}
            key={whitelist.id}
          >
            {whiteListItemBeingEdited.id === whitelist.id ? (
              <div className="flex flex-col">
                <input
                  type="text"
                  className="p-1 border border-gray-300 rounded"
                  defaultValue={whitelist.email_or_domain}
                  onChange={e =>
                    setWhiteListItemBeingEdited({
                      ...whiteListItemBeingEdited,
                      emailOrDomain: e.target.value
                    })
                  }
                  disabled={whiteListItemBeingEdited.isLoading}
                />
                {!whiteListItemBeingEdited.emailOrDomainValid && (
                  <span className="italic text-red-600">Please enter a valid email or domain</span>
                )}
              </div>
            ) : (
              <span>{whitelist.email_or_domain}</span>
            )}
            <div className="flex items-start">
              {whiteListItemBeingEdited.id === whitelist.id ? (
                <>
                  <button
                    onClick={() =>
                      setWhiteListItemBeingEdited({
                        id: -1,
                        isLoading: false,
                        emailOrDomain: "",
                        emailOrDomainValid: true
                      })
                    }
                    className={`text-red-600 mr-4 hover:text-red-600 hover:scale-110 transition-transform ${
                      whiteListItemBeingEdited.isLoading && "cursor-progress"
                    }`}
                    disabled={whiteListItemBeingEdited.isLoading}
                  >
                    Discard
                  </button>
                  <button
                    onClick={onWhiteListEdit(whitelist)}
                    className={`hover:text-blue-500 hover:scale-110 transition-transform ${
                      whiteListItemBeingEdited.isLoading && "cursor-progress"
                    }`}
                    disabled={whiteListItemBeingEdited.isLoading}
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <EditIcon
                    onClick={() =>
                      setWhiteListItemBeingEdited({
                        id: whitelist.id,
                        isLoading: false,
                        emailOrDomain: whitelist.email_or_domain,
                        emailOrDomainValid: true
                      })
                    }
                    className="mr-4 cursor-pointer hover:text-blue-600 hover:scale-125 hover:shadow-lg transition-transform"
                  />
                  <DeleteIcon
                    onClick={onWhiteListDelete(whitelist)}
                    className="cursor-pointer hover:text-blue-600 hover:scale-125 hover:shadow-lg transition-transform"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      {(customerDetailEntity?.whitelists?.data || []).length > 2 && (
        <div className="flex justify-center border-t border-gray-300 py-2">
          <button onClick={() => setViewMore(!viewMore)}>
            {viewMore ? (
              <span className="flex items-center text-blue-600">
                <img src={ViewLessIcon} alt="View less" className="mr-2" />
                View less
              </span>
            ) : (
              <span className="flex items-center text-blue-600">
                <img src={ViewMoreIcon} alt="View more" className="mr-2" />
                View more
              </span>
            )}
          </button>
        </div>
      )}
      {customerIsLoading &&
        [1, 2, 3].map(index => (
          <div key={index} className="p-2 border-t border-gray-300 flex items-center animate-pulse">
            <div className="bg-slate-200 h-6 w-20 rounded-lg" />
          </div>
        ))}
      {!customerIsLoading && customerDetailEntity?.whitelists?.data.length === 0 && (
        <div className="p-2 border-t border-gray-300 flex flex-col justify-center items-center text-center text-gray-600">
          <img
            src={ClientPortalEmailIcon}
            alt="Client portal Emails or domain"
            className="w-24 mt-6"
          />
          <span>
            Add the Client’s Email or their company Domain to provide them access for the portal.
          </span>
          <span>
            On adding the client's domain name, anyone with an address in that domain can login
          </span>
        </div>
      )}
      <span className="p-2 border-t border-gray-300 flex text-gray-700">Client Portal Link</span>
      <div
        onClick={portalLinkCopyToClipboard}
        className="group flex p-2 rounded bg-[#F0F5FF] items-center cursor-pointer"
      >
        {teamIsLoading ? (
          <div className="bg-slate-200 h-6 text-slate-200 animate-pulse rounded-lg"></div>
        ) : (
          <>
            <a href={portalLink} onClick={e => e.preventDefault()} className="mr-4 break-all">
              {portalLink}
            </a>
            <div className="col-span-1">
              <CopyButtonIcon className="group-hover:text-blue-500 h-4 w-4" />
            </div>
          </>
        )}
      </div>
      {showPopover && (
        <div className="p-2 text-gray-500">
          Your client portal link has been copied to the clipboard!
        </div>
      )}
    </div>
  );
};

export default CustomerClientPortalCard;
